import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import {
  ApiError,
  getTimetableCacheKeys,
  getTimetablesCacheKeys,
  GroupType,
  Period,
  TimeSlot,
  TimetablePerformActionEnum,
  WeekdayEnum,
} from '@schooly/api';
import { useConfirmationDialog } from '@schooly/components/confirmation-dialog';
import { TIMETABLE } from '@schooly/constants/test-ids';
import { useNotifications } from '@schooly/components/notifications';
import { useFlag } from '@schooly/hooks/use-flag';
import {
  CheckIcon,
  Counter,
  CrossIcon,
  DeleteIcon,
  EditIcon,
  ModalContent,
  ModalFooter,
  ModalLarge,
  Spin,
  TagSubject,
  TypographyWithOverflowHint,
} from '@schooly/style';
import { setFormErrorForApiError } from '@schooly/utils/set-form-error-for-api-error';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form-lts';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { ControlModalHeaderInput } from '../../../../components/uikit-components/Modal/ControlModalHeaderInput';
import { ModalHeaderV2 } from '../../../../components/uikit-components/Modal/ModalHeaderV2';
import { useTimetable } from '../../../../context/timetable/WithTimetable';
import { TimetableAllocationDetailed, TimetableForm, TimetableGroup } from '../types';
import { TimetableGridFilters } from '../utils';
import {
  buildSlotRoomMap,
  getTimetableAllocationsDetailed,
  hasPublishedTimetable,
  isLessonsEmpty,
  transformFormToRequest,
  transformTimetableToForm,
} from '../utils';
import { AddGroupModal } from './AddGroupModal';
import {
  GroupConflict,
  removeGroupsFromSlotAllocations,
  selectConflictingScheduledGroups,
} from './groupConflicts';
import { TimetableGrid } from './TimetableGrid';
import { TimetableGridFiltersBar } from './TimetableGridFiltersBar';
import { TimetableGridFiltersPopup } from './TimetableGridFiltersPopup';
import {
  TimetableGroupConflictsContextValue,
  TimetableGroupConflictsProvider,
} from './TimetableGroupConflictsContext';
import { usePublishedGroupConflicts } from './usePublishedGroupConflicts';

export type SelectedSlotInfo = {
  timeSlot: TimeSlot;
  period: Period;
  weekday: WeekdayEnum;
};

type ConflictingGroup = { group: TimetableGroup; conflict: GroupConflict };

type TimetableModalContentProps = {
  onClose: () => void;
  mode: 'view' | 'edit';
};

export const TimetableModalContent: FC<TimetableModalContentProps> = ({ onClose, mode }) => {
  const { formatMessage } = useIntl();
  const {
    timetable,
    isSaving,
    isDeleting,
    isPerformingAction,
    createTimetable,
    updateTimetable,
    removeTimetable,
    structure,
    performTimetableAction,
    schoolYear,
    schoolId,
  } = useTimetable();
  const queryClient = useQueryClient();
  const { showNotification } = useNotifications();
  const [selectedSlot, setSelectedSlot] = useState<SelectedSlotInfo | null>(null);
  const [gridFilters, setGridFilters] = useState<TimetableGridFilters>({});
  const [isFiltersPopupOpen, openFiltersPopup, closeFiltersPopup] = useFlag(false);
  const { getConfirmation } = useConfirmationDialog();
  const form = useForm<TimetableForm>({
    defaultValues: timetable
      ? transformTimetableToForm(timetable)
      : { name: '', slotAllocations: {}, groups: [], rooms: [] },
  });

  const currentStructureId = timetable?.structure.id ?? structure.id;
  const { conflictMap, isLoading: isLoadingGroupConflicts } = usePublishedGroupConflicts({
    schoolId,
    schoolYearId: schoolYear.id,
    currentStructureId,
  });

  const groupConflictsValue = useMemo<TimetableGroupConflictsContextValue>(
    () => ({
      getGroupConflict: (groupId: string) => conflictMap.get(groupId),
    }),
    [conflictMap],
  );

  const timetableFormErrorOptions = {
    field: 'name' as const,
    errorToTranslationKey: {
      TIMETABLE_NAME_UNIQUENESS_ERROR: 'school-timetables-UniqueNameError',
    },
  };
  const isMutating = isSaving || isDeleting || isPerformingAction;

  const invalidateTimetableList = useCallback(() => {
    queryClient.invalidateQueries(
      getTimetablesCacheKeys({ school_year_id: schoolYear.id, school_id: schoolId }),
    );
    queryClient.invalidateQueries(getTimetablesCacheKeys({ school_id: schoolId }));
  }, [queryClient, schoolYear.id, schoolId]);

  const name = form.watch('name');
  const groups = form.watch('groups');
  const rooms = form.watch('rooms');
  const slotAllocations = form.watch('slotAllocations');

  const slotRoomMap = useMemo(
    () => buildSlotRoomMap(slotAllocations, rooms),
    [slotAllocations, rooms],
  );

  const getConflictingGroups = useCallback(
    (data: TimetableForm): ConflictingGroup[] => {
      const groupById = new Map(data.groups.map((group) => [group.id, group]));
      return selectConflictingScheduledGroups(data.slotAllocations, conflictMap).reduce<
        ConflictingGroup[]
      >((acc, { groupId, conflict }) => {
        const group = groupById.get(groupId);
        if (group) acc.push({ group, conflict });
        return acc;
      }, []);
    },
    [conflictMap],
  );

  const removeConflictingGroups = (
    data: TimetableForm,
    conflictingGroups: ConflictingGroup[],
  ): TimetableForm => ({
    ...data,
    slotAllocations: removeGroupsFromSlotAllocations(
      data.slotAllocations,
      new Set(conflictingGroups.map(({ group }) => group.id)),
    ),
  });

  const handleSave = async (
    data: TimetableForm,
    withSuccessNotification = true,
    onCloseCallback = () => {},
  ) => {
    const requestData = transformFormToRequest(data);

    const showSuccessNotificationAndClose = () => {
      if (!withSuccessNotification) return;
      showNotification({
        type: 'success',
        message: formatMessage(
          {
            id: timetable
              ? 'timetable-notification-SuccessfullyUpdated'
              : 'timetable-notification-SuccessfullySaved',
          },
          { name },
        ),
      });
      invalidateTimetableList();
    };

    try {
      if (!timetable) {
        const res = await createTimetable(schoolYear.id, requestData.slot_allocations, data.name);
        showSuccessNotificationAndClose();
        onCloseCallback();
        return res;
      } else {
        const res = await updateTimetable(timetable.id, requestData);
        showSuccessNotificationAndClose();
        onCloseCallback();
        return res;
      }
    } catch (e) {
      setFormErrorForApiError(e as ApiError, form, timetableFormErrorOptions, (translationKey) =>
        formatMessage({ id: translationKey }),
      );
      return null;
    }
  };

  const handlePerformTimetableAction = async (
    data: TimetableForm,
    action: TimetablePerformActionEnum,
  ) => {
    if (action === TimetablePerformActionEnum.Unpublish) {
      const isConfirmed = await getConfirmation({
        textId: 'timetable-notification-UnpublishConfirmation',
        textValues: { name: data.name },
      });

      if (!isConfirmed) return;

      const currentTimetable = await handleSave(data, false);
      if (!currentTimetable) return;

      await performTimetableAction(currentTimetable.id, action);
      invalidateTimetableList();
      queryClient.invalidateQueries(getTimetableCacheKeys({ timetableId: currentTimetable.id }));
      showNotification({
        type: 'success',
        textId: 'timetable-notification-SuccessfullyUnpublished',
        values: { name },
      });

      onClose();
      return;
    }

    const isEmptyLessons = isLessonsEmpty(data.slotAllocations);
    const hasPreviousTimetablePublished = hasPublishedTimetable(
      timetable ? timetable.structure : structure,
      schoolYear.id,
    );

    const conflictingGroups = getConflictingGroups(data);

    const isConfirmed = await getConfirmation({
      content: (
        <TimetablePublishConfirmationContent
          isEmptyLessons={isEmptyLessons}
          hasPreviousTimetablePublished={hasPreviousTimetablePublished}
          conflictingGroups={conflictingGroups}
          timetableName={data.name}
        />
      ),
      ...(conflictingGroups.length > 0 ? { confirmTextId: 'action-PublishAnyway' } : {}),
    });

    if (!isConfirmed) return;

    const dataToPublish =
      conflictingGroups.length > 0 ? removeConflictingGroups(data, conflictingGroups) : data;

    const currentTimetable = await handleSave(dataToPublish, false);
    if (!currentTimetable) return;

    await performTimetableAction(currentTimetable.id, action);
    showNotification({
      type: 'success',
      textId: 'timetable-notification-SuccessfullyPublished',
      values: { name },
    });
    invalidateTimetableList();
    queryClient.invalidateQueries(getTimetableCacheKeys({ timetableId: currentTimetable.id }));
    onClose();
  };

  const handleSaveClick = async (data: TimetableForm) => {
    let dataToSave = data;

    if (timetable?.published_at) {
      const conflictingGroups = getConflictingGroups(data);

      if (conflictingGroups.length > 0) {
        const isConfirmed = await getConfirmation({
          content: (
            <GroupConflictsRemovalContent
              conflictingGroups={conflictingGroups}
              timetableName={data.name}
              descriptionId="timetable-group-conflict-RemovalDescriptionOnSave"
            />
          ),
          confirmTextId: 'action-SaveAnyway',
        });

        if (!isConfirmed) return;

        dataToSave = removeConflictingGroups(data, conflictingGroups);
      }
    }

    await handleSave(dataToSave, true, onClose);
  };

  const handleDelete = useCallback(async () => {
    if (!timetable?.id) return;

    const isConfirmed = await getConfirmation({
      textId: 'timetable-notification-Delete',
      textValues: { name },
    });

    if (!isConfirmed) return;

    await removeTimetable(timetable.id);
    invalidateTimetableList();
    onClose();
  }, [getConfirmation, onClose, removeTimetable, timetable?.id, name, invalidateTimetableList]);

  const handleSlotClick = useCallback(
    (timeSlotId: string) => {
      if (!structure) return;

      const timeSlot = structure.time_slots.find((s) => s.id === timeSlotId);
      const period = structure.periods.find((p) => p.id === timeSlot?.period_id);

      if (!period || !timeSlot) return;

      setSelectedSlot({
        timeSlot,
        period,
        weekday: timeSlot.weekday,
      });
    },
    [structure],
  );

  const handleCloseAddGroupModal = useCallback(() => {
    setSelectedSlot(null);
  }, []);

  const handleSaveAllocations = useCallback(
    (allocations: TimetableAllocationDetailed[]) => {
      if (!selectedSlot) return;

      form.setValue(
        `slotAllocations.${selectedSlot.timeSlot.id}`,
        allocations.map((a) => ({
          group_id: a.group.id,
          room_id: a.room?.id ?? null,
        })),
        { shouldDirty: true },
      );

      const groups = new Map(form.getValues('groups').map((g) => [g.id, g]));
      allocations.forEach((a) => groups.set(a.group.id, a.group));
      form.setValue('groups', Array.from(groups.values()), {
        shouldDirty: true,
      });

      const rooms = new Map(form.getValues('rooms').map((r) => [r.id, r]));
      allocations.forEach((a) => a.room && rooms.set(a.room.id, a.room));
      form.setValue('rooms', Array.from(rooms.values()), {
        shouldDirty: true,
      });

      setSelectedSlot(null);
    },
    [selectedSlot, form],
  );

  const renderFooter = () => {
    const notPublishedTimetable = !timetable || !timetable?.published_at;
    const iconForPublishButton = notPublishedTimetable ? <CheckIcon /> : undefined;

    const performTimetableActionButton = (
      <Button
        startIcon={isPerformingAction ? <Spin /> : iconForPublishButton}
        onClick={() =>
          form.handleSubmit((d) =>
            handlePerformTimetableAction(
              d,
              timetable?.published_at
                ? TimetablePerformActionEnum.Unpublish
                : TimetablePerformActionEnum.Publish,
            ),
          )()
        }
        variant={notPublishedTimetable ? 'contained' : 'outlined'}
        disabled={isMutating || isLoadingGroupConflicts}
      >
        {formatMessage({ id: timetable?.published_at ? 'reports-Unpublish' : 'action-Publish' })}
      </Button>
    );

    const iconForSaveButton = timetable?.published_at ? <CheckIcon /> : undefined;

    const removeTimetableButton = timetable ? (
      <Button
        variant="outlined"
        color="error"
        onClick={handleDelete}
        startIcon={isDeleting ? <Spin /> : <DeleteIcon />}
        sx={{ mr: 'auto' }}
        disabled={isMutating}
      >
        {formatMessage({ id: 'action-Delete' })}
      </Button>
    ) : null;

    return (
      <>
        {removeTimetableButton}
        {timetable?.published_at && performTimetableActionButton}
        <Button
          startIcon={isSaving ? <Spin /> : iconForSaveButton}
          variant={!timetable?.published_at ? 'outlined' : 'contained'}
          disabled={isMutating || (!!timetable?.published_at && isLoadingGroupConflicts)}
          onClick={() => form.handleSubmit(handleSaveClick)()}
        >
          {formatMessage({ id: 'action-Save' })}
        </Button>
        {notPublishedTimetable && performTimetableActionButton}
      </>
    );
  };

  const handleClose = useCallback(async () => {
    if (!form.formState.isDirty) {
      onClose();
      return;
    }

    const isConfirmed = await getConfirmation({
      textId: 'school-timetableStructures-CloseConfirmation',
    });

    if (isConfirmed) {
      onClose();
    }
  }, [form.formState.isDirty, getConfirmation, onClose]);

  const schoolYearName = schoolYear.name;

  return (
    <TimetableGroupConflictsProvider value={groupConflictsValue}>
      <ModalLarge open onClose={handleClose}>
        <FormProvider {...form}>
          <form
            onSubmit={
              mode === 'edit' ? form.handleSubmit(handleSaveClick) : (e) => e.preventDefault()
            }
          >
            <ModalHeaderV2
              active
              title={
                mode === 'view' ? (
                  <Typography variant="h2">{timetable?.name ?? ''}</Typography>
                ) : (
                  <Stack
                    direction="row"
                    alignItems="center"
                    gap={1}
                    component="label"
                    sx={{
                      cursor: 'pointer',
                      '&:hover .edit-icon': {
                        visibility: 'visible',
                      },
                      mb: 0,
                    }}
                  >
                    <ControlModalHeaderInput
                      control={form.control}
                      name="name"
                      rules={{ required: true }}
                      autoFocus={!name}
                      placeholder={formatMessage({ id: 'timetable-name-input' })}
                      id="timetable-name-input"
                      inputProps={{ 'data-test-id': TIMETABLE.NAME_INPUT }}
                      fullWidth
                    />

                    <IconButton
                      className="edit-icon"
                      size="small"
                      sx={{
                        visibility: 'hidden',
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        document.getElementById('timetable-name-input')?.focus();
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  </Stack>
                )
              }
              multiline
              subHeader={
                <Stack direction="row" gap={5}>
                  <Typography color="common.grey2">{structure?.name ?? ''}</Typography>
                  <Typography color="common.grey2">{schoolYearName}</Typography>
                </Stack>
              }
            >
              <IconButton onClick={handleClose}>
                <CrossIcon />
              </IconButton>
            </ModalHeaderV2>

            <ModalContent active sx={{ paddingLeft: 0 }}>
              <Stack px={2.5} pb={2.5}>
                <TimetableGridFiltersBar filters={gridFilters} onOpenFilters={openFiltersPopup} />
              </Stack>
              {structure && (
                <TimetableGrid
                  structure={structure}
                  slotAllocations={form.watch('slotAllocations')}
                  slotRoomMap={slotRoomMap}
                  groups={groups}
                  filters={gridFilters}
                  onSlotClick={handleSlotClick}
                  isEditable={mode === 'edit'}
                />
              )}
            </ModalContent>
            {mode === 'view' ? null : <ModalFooter active>{renderFooter()}</ModalFooter>}
          </form>
        </FormProvider>

        {isFiltersPopupOpen && (
          <TimetableGridFiltersPopup
            filters={gridFilters}
            availableAgeGroupIds={structure?.age_group_ids ?? []}
            onClose={closeFiltersPopup}
            onSetFilters={setGridFilters}
          />
        )}

        {selectedSlot && (
          <AddGroupModal
            slot={selectedSlot}
            initialAllocations={getTimetableAllocationsDetailed({
              allocations: slotAllocations[selectedSlot.timeSlot.id] ?? [],
              groups,
              rooms,
            })}
            onSave={handleSaveAllocations}
            onClose={handleCloseAddGroupModal}
            schoolId={schoolId}
            dateRange={[schoolYear.start, schoolYear.end]}
          />
        )}
      </ModalLarge>
    </TimetableGroupConflictsProvider>
  );
};

const TimetablePublishConfirmationContent: FC<{
  isEmptyLessons: boolean;
  hasPreviousTimetablePublished: boolean;
  conflictingGroups: ConflictingGroup[];
  timetableName: string;
}> = ({ isEmptyLessons, hasPreviousTimetablePublished, conflictingGroups, timetableName }) => {
  const { formatMessage } = useIntl();

  if (conflictingGroups.length > 0) {
    return (
      <GroupConflictsRemovalContent
        conflictingGroups={conflictingGroups}
        timetableName={timetableName}
      />
    );
  }

  return (
    <Stack gap={5}>
      {isEmptyLessons && (
        <Typography variant="h1">
          {formatMessage({ id: 'timetable-notification-empty-lessons-PublishConfirmationMain' })}
        </Typography>
      )}
      <Typography variant="h1">
        {isEmptyLessons
          ? formatMessage({
              id: hasPreviousTimetablePublished
                ? 'timetable-notification-empty-lessons-PublishConfirmationWithPreviousPublication'
                : 'timetable-notification-PublishConfirmationContent',
            })
          : formatMessage({
              id: hasPreviousTimetablePublished
                ? 'timetable-notification-PublishConfirmationWithPreviousPublication'
                : 'timetable-notification-PublishConfirmationContent',
            })}
      </Typography>
      <Typography variant="h1">
        {formatMessage({ id: 'timetable-notification-PublishConfirmationFooter' })}
      </Typography>
    </Stack>
  );
};

const GroupConflictsRemovalContent: FC<{
  conflictingGroups: ConflictingGroup[];
  timetableName: string;
  descriptionId?: string;
}> = ({
  conflictingGroups,
  timetableName,
  descriptionId = 'timetable-group-conflict-RemovalDescription',
}) => {
  const { formatMessage } = useIntl();

  return (
    <Stack gap={2.5}>
      <Typography variant="h1">
        {formatMessage(
          { id: 'timetable-group-conflict-RemovalTitle' },
          { count: conflictingGroups.length },
        )}
      </Typography>

      <Typography color="common.grey2">
        {formatMessage({ id: descriptionId }, { name: timetableName })}
      </Typography>

      <Stack
        gap={0.5}
        sx={(theme) => ({ p: 2, borderRadius: 1, backgroundColor: theme.palette.common.lightBg })}
      >
        <Typography variant="h3">
          {formatMessage({ id: 'timetable-group-conflict-WhatCanIDo' })}
        </Typography>
        <Typography color="common.grey2">
          {formatMessage({ id: 'timetable-group-conflict-RemovalHint' })}
        </Typography>
      </Stack>

      <Stack>
        {conflictingGroups.map(({ group, conflict }) => (
          <Stack
            key={group.id}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
            sx={(theme) => ({
              py: 1.5,
              borderBottom: `1px solid ${theme.palette.common.light3}`,
            })}
          >
            <Stack direction="row" alignItems="center" gap={0.5} sx={{ minWidth: 0, flex: 1 }}>
              <TypographyWithOverflowHint
                variant="h3"
                color="common.grey2"
                noWrap
                component={Link}
                to={`/groups/${group.id}`}
                target="_blank"
                sx={{ '&:hover': { textDecoration: 'underline' } }}
              >
                {group.name}
              </TypographyWithOverflowHint>
              <Counter
                disabled
                sx={{
                  minWidth: 16,
                  maxHeight: 16,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Typography variant="body3" color="common.main3">
                  {group.student_count ?? 0}
                </Typography>
              </Counter>
            </Stack>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              {(group.subject || group.type === GroupType.TutorGroup) && (
                <TagSubject
                  sx={{
                    maxWidth: '100%',
                    '& .MuiTypography-root': { overflow: 'hidden', textOverflow: 'ellipsis' },
                  }}
                >
                  {group.type === GroupType.TutorGroup
                    ? formatMessage({ id: 'timetable-group-conflict-TutorGroup' })
                    : group.subject?.name}
                </TagSubject>
              )}
            </Box>

            <TypographyWithOverflowHint
              variant="h3"
              color="primary.main"
              noWrap
              component={Link}
              to={`/settings/timetables/${conflict.timetableId}/edit`}
              target="_blank"
              sx={{ flex: 1, '&:hover': { textDecoration: 'underline' } }}
            >
              {conflict.timetableName}
            </TypographyWithOverflowHint>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};
